import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import AddressManager from '../components/AddressManager';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Settings, MapPin, User, Bell, CreditCard, Shield, 
  ChevronRight, Check, Save
} from 'lucide-react';

const SettingsPage = () => {
  const { user, isAuthenticated, refreshUser, api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  
  // Profile edit state
  const [profileForm, setProfileForm] = useState({
    name: '',
    bio: '',
    seller_zip: ''
  });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/settings');
      return;
    }
    
    if (user) {
      setProfileForm({
        name: user.name || '',
        bio: user.bio || '',
        seller_zip: user.seller_zip || ''
      });
    }
  }, [isAuthenticated, user, navigate]);
  
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };
  
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/auth/me', profileForm);
      await refreshUser();
      toast.success('Profile updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };
  
  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'addresses', label: 'Shipping Addresses', icon: MapPin },
    { id: 'notifications', label: 'Notifications', icon: Bell, disabled: true },
    { id: 'payment', label: 'Payment Methods', icon: CreditCard, disabled: true },
    { id: 'security', label: 'Security', icon: Shield, disabled: true },
  ];
  
  if (!isAuthenticated) {
    return null;
  }
  
  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Settings className="w-8 h-8 text-[#00BFFF]" />
            Settings
          </h1>
          <p className="text-zinc-400 mt-2">
            Manage your account settings and preferences
          </p>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <nav className="space-y-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => !tab.disabled && handleTabChange(tab.id)}
                  disabled={tab.disabled}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#00BFFF]/10 text-[#00BFFF] border border-[#00BFFF]/30'
                      : tab.disabled
                        ? 'text-zinc-600 cursor-not-allowed'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                  data-testid={`settings-tab-${tab.id}`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="flex-1">{tab.label}</span>
                  {tab.disabled && (
                    <span className="text-xs text-zinc-600">Soon</span>
                  )}
                  {activeTab === tab.id && (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              ))}
            </nav>
          </div>
          
          {/* Content Area */}
          <div className="flex-1">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-white mb-1">Profile Information</h2>
                    <p className="text-zinc-400 text-sm">
                      Update your display name and seller information
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-zinc-300">Display Name</Label>
                      <Input
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                        className="mt-1 bg-zinc-800 border-zinc-700"
                        placeholder="Your name"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-zinc-300">Bio</Label>
                      <textarea
                        value={profileForm.bio}
                        onChange={(e) => setProfileForm({...profileForm, bio: e.target.value})}
                        className="w-full mt-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none"
                        rows={3}
                        placeholder="Tell others about yourself..."
                      />
                    </div>
                    
                    <div>
                      <Label className="text-zinc-300">Seller ZIP Code</Label>
                      <Input
                        value={profileForm.seller_zip}
                        onChange={(e) => setProfileForm({...profileForm, seller_zip: e.target.value})}
                        className="mt-1 bg-zinc-800 border-zinc-700 max-w-xs"
                        placeholder="12345"
                        maxLength={10}
                      />
                      <p className="text-zinc-500 text-xs mt-1">
                        Used for calculating shipping rates for your listings
                      </p>
                    </div>
                    
                    <div className="pt-4">
                      <Button 
                        onClick={handleSaveProfile}
                        className="bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black"
                        disabled={saving}
                      >
                        {saving ? (
                          <>Saving...</>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Addresses Tab */}
              {activeTab === 'addresses' && (
                <AddressManager />
              )}
              
              {/* Notifications Tab (Coming Soon) */}
              {activeTab === 'notifications' && (
                <div className="text-center py-12">
                  <Bell className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">Coming Soon</h3>
                  <p className="text-zinc-400">
                    Notification preferences will be available soon.
                  </p>
                </div>
              )}
              
              {/* Payment Tab (Coming Soon) */}
              {activeTab === 'payment' && (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">Coming Soon</h3>
                  <p className="text-zinc-400">
                    Payment method management will be available soon.
                  </p>
                </div>
              )}
              
              {/* Security Tab (Coming Soon) */}
              {activeTab === 'security' && (
                <div className="text-center py-12">
                  <Shield className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">Coming Soon</h3>
                  <p className="text-zinc-400">
                    Security settings will be available soon.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
